/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * My profile › Account: the guarded account deletion (F032 D25, LT-2003).
 * The server decides; this shows its answer: hand the gym over, sort out a
 * live membership, sign in again, or type "delete" to confirm.
 */
import React, { useContext, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
} from "@lemonadetraining/web-ui";
import {
  ACCOUNT_DELETION_REFUSAL_REASON,
  accountDeletionMembershipSentence,
  accountDeletionOwnsGymSentence,
  type AccountDeletionLiveMembership,
  type AccountDeletionRefusalDetails,
  type DeleteMyAccountMode,
  type DeleteMyAccountResponse,
} from "@lemonadetraining/contracts";
import {
  AuthContext,
  deleteMyAccount,
  NOTIFICATION_TYPE,
  notifyActions,
  useAppDispatch,
} from "@lemonadetraining/web-shared";
import { formatCommitmentDate } from "@lemonadetraining/web-shared/utils/commitmentStatus";
import { useOwnGymZone } from "../../hooks/useOwnGymZone";
import { withPendingDate } from "../time/PendingDate";

const CONFIRM_WORD = "delete";
const SUPPORT_HANDOVER = "mailto:info@lemonade.training?subject=Hand%20over%20my%20gym";

type View =
  | { kind: "closed" }
  | { kind: "confirm" }
  | { kind: "reauth" }
  | { kind: "refused"; details: AccountDeletionRefusalDetails };

/** The refusal the server put in `details`, or null for any other failure. */
export const refusalOf = (error: unknown): AccountDeletionRefusalDetails | null => {
  const details = (error as { details?: { reason?: unknown } } | null)?.details;
  const reasons: unknown[] = Object.values(ACCOUNT_DELETION_REFUSAL_REASON);
  return details && reasons.includes(details.reason)
    ? (details as AccountDeletionRefusalDetails)
    : null;
};

const membershipSentence = (
  m: AccountDeletionLiveMembership,
  zone: string | null | undefined
): React.ReactNode => {
  if (!m.commitmentEndsAt) return accountDeletionMembershipSentence(m, null);
  const end = zone === undefined ? null : formatCommitmentDate(new Date(m.commitmentEndsAt), zone);
  return withPendingDate((date) => accountDeletionMembershipSentence(m, date), end);
};

interface AccountSectionProps {
  /** Opens My profile › Memberships. */
  onGoToMemberships: () => void;
}

const AccountSection: React.FC<AccountSectionProps> = ({ onGoToMemberships }) => {
  const dispatch = useAppDispatch();
  const authCtx = useContext(AuthContext) as unknown as { userLogOut: () => Promise<void> };
  const [view, setView] = useState<View>({ kind: "closed" });
  const { zone } = useOwnGymZone(
    view.kind === "refused" &&
      view.details.reason === ACCOUNT_DELETION_REFUSAL_REASON.HAS_LIVE_MEMBERSHIP
  );
  const [busy, setBusy] = useState<DeleteMyAccountMode | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [typed, setTyped] = useState("");

  const leaveAfterDeletion = async () => {
    dispatch(
      notifyActions.sendNotification({
        severity: NOTIFICATION_TYPE.success,
        title: "Account deleted",
        message: "Your account and your data are being removed.",
      })
    );
    sessionStorage.setItem("isLoggingOut", "true");
    try {
      await authCtx.userLogOut();
    } catch (error) {
      // The account is gone either way; the website signs out on arrival.
      console.error("Logout after deletion failed:", error);
    }
    sessionStorage.removeItem("isLoggingOut");
    const website = new URL(process.env.REACT_APP_WEBSITE_URL || "http://localhost:3000");
    website.searchParams.set("logout", "success");
    window.location.href = website.toString();
  };

  const run = async (mode: DeleteMyAccountMode) => {
    setBusy(mode);
    setFailed(null);
    let result: DeleteMyAccountResponse;
    try {
      result = (await deleteMyAccount(mode)) as DeleteMyAccountResponse;
    } catch (error) {
      const refusal = refusalOf(error);
      if (refusal?.reason === ACCOUNT_DELETION_REFUSAL_REASON.REQUIRES_RECENT_LOGIN) {
        setView({ kind: "reauth" });
      } else if (refusal) {
        setView({ kind: "refused", details: refusal });
      } else {
        setFailed(
          mode === "check"
            ? "We couldn't check your account. Please try again."
            : "We couldn't finish deleting your account. Reload the page, and if you're still signed in, try again."
        );
      }
      setBusy(null);
      return;
    }
    if (result.outcome === "allowed") {
      setTyped("");
      setView({ kind: "confirm" });
      setBusy(null);
      return;
    }
    await leaveAfterDeletion();
  };

  const signInAgain = async () => {
    const websiteUrl = process.env.REACT_APP_WEBSITE_URL || "http://localhost:3000";
    const appUrl = process.env.REACT_APP_APP_URL || "http://localhost:3001";
    const login = new URL(`${websiteUrl}/login`);
    login.searchParams.set("logout", "success");
    login.searchParams.set("returnUrl", `${appUrl}/dashboard/profile?tab=account`);
    sessionStorage.setItem("isLoggingOut", "true");
    try {
      await authCtx.userLogOut();
    } catch (error) {
      console.error("Logout error:", error);
    }
    sessionStorage.removeItem("isLoggingOut");
    window.location.href = login.toString();
  };

  const close = () => {
    setView({ kind: "closed" });
    setFailed(null);
  };

  const renderDialog = () => {
    if (view.kind === "refused" && view.details.reason === "owns_gym") {
      return (
        <>
          <DialogHeader>
            <DialogTitle>Hand the gym over first</DialogTitle>
            <DialogDescription className="text-foreground">
              {accountDeletionOwnsGymSentence(view.details.gyms)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={close}>
              Close
            </Button>
            <Button asChild className="min-h-[44px]">
              <a href={SUPPORT_HANDOVER} data-testid="account-delete-contact">
                Contact us
              </a>
            </Button>
          </DialogFooter>
        </>
      );
    }
    if (view.kind === "refused" && view.details.reason === "has_live_membership") {
      const { memberships } = view.details;
      const onlyCancel = memberships.every((m) => m.block === "cancel_first");
      return (
        <>
          <DialogHeader>
            <DialogTitle>
              {onlyCancel ? "Cancel your membership first" : "Your membership comes first"}
            </DialogTitle>
            <DialogDescription className="text-foreground">
              Deleting your account would stop your payments to your gym, so this has to be sorted
              out first.
            </DialogDescription>
          </DialogHeader>
          <ul
            className="flex list-disc flex-col gap-1 pl-5 text-sm"
            data-testid="account-delete-memberships"
          >
            {memberships.map((m) => (
              <li key={m.subscriptionId}>{membershipSentence(m, zone)}</li>
            ))}
          </ul>
          <p className="text-sm text-muted-foreground">
            Visits left on a visit card are lost when you delete your account.
          </p>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={close}>
              Close
            </Button>
            <Button
              className="min-h-[44px]"
              onClick={() => {
                close();
                onGoToMemberships();
              }}
              data-testid="account-delete-go-memberships"
            >
              Go to Memberships
            </Button>
          </DialogFooter>
        </>
      );
    }
    if (view.kind === "reauth") {
      return (
        <>
          <DialogHeader>
            <DialogTitle>Sign in again to continue</DialogTitle>
            <DialogDescription className="text-foreground">
              For your safety, deleting your account needs a fresh sign-in. Sign in again, and
              you&apos;ll come straight back here. Nothing has been deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={close}>
              Keep my account
            </Button>
            <Button
              className="min-h-[44px]"
              onClick={signInAgain}
              data-testid="account-delete-reauth"
            >
              Sign in again
            </Button>
          </DialogFooter>
        </>
      );
    }
    const confirmed = typed.trim().toLowerCase() === CONFIRM_WORD;
    return (
      <>
        <DialogHeader>
          <DialogTitle>Delete your account?</DialogTitle>
          <DialogDescription className="text-foreground">This removes, for good:</DialogDescription>
        </DialogHeader>
        <ul className="flex list-disc flex-col gap-1 pl-5 text-sm">
          <li>your profile and photo</li>
          <li>your training history, results and records</li>
          <li>your upcoming bookings and waiting-list places (they go back to your gym)</li>
        </ul>
        <p className="text-sm text-muted-foreground">This can&apos;t be undone.</p>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="account-delete-confirm">
            Type <span className="font-semibold">{CONFIRM_WORD}</span> to confirm
          </Label>
          <Input
            id="account-delete-confirm"
            autoComplete="off"
            value={typed}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTyped(e.target.value)}
            data-testid="account-delete-input"
          />
        </div>
        {failed && (
          <p role="alert" className="text-sm text-destructive">
            {failed}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" className="min-h-[44px]" onClick={close}>
            Keep my account
          </Button>
          <Button
            variant="destructive"
            className="min-h-[44px]"
            disabled={!confirmed || busy !== null}
            aria-busy={busy === "delete"}
            onClick={() => run("delete")}
            data-testid="account-delete-submit"
          >
            {busy === "delete" && <Spinner className="h-4 w-4" />}
            Delete my account
          </Button>
        </DialogFooter>
      </>
    );
  };

  return (
    <Card data-testid="account-section">
      <CardHeader>
        <CardTitle>Delete my account</CardTitle>
        <CardDescription>
          Removes your profile, your training history and your records for good.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-start gap-3">
        <p className="text-sm text-muted-foreground">
          Your gym keeps its own records of payments, as the law requires.
        </p>
        <Button
          variant="destructive"
          className="min-h-[44px]"
          disabled={busy !== null}
          aria-busy={busy === "check"}
          onClick={() => run("check")}
          data-testid="account-delete-open"
        >
          {busy === "check" && <Spinner className="h-4 w-4" />}
          Delete my account
        </Button>
        {failed && view.kind === "closed" && (
          <p role="alert" className="text-sm text-destructive">
            {failed}
          </p>
        )}
      </CardContent>

      <Dialog open={view.kind !== "closed"} onOpenChange={(open: boolean) => !open && close()}>
        <DialogContent
          role="alertdialog"
          className="max-w-[min(30rem,calc(100%-2rem))]"
          data-testid="account-delete-dialog"
        >
          {renderDialog()}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AccountSection;
